// Modal that opens when the user marks a grocery item as bought. Pre-fills
// from the grocery item's data, lets them adjust quantity / unit / location /
// best-before, and supports scanning a barcode if they bought a substitute
// product.

import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { BarcodeScanner } from "./BarcodeScanner";
import type { GroceryEntry } from "../types";

interface Props {
  open: boolean;
  item: GroceryEntry | null;
  suggestions: { locations: string[]; categories: string[] };
  onClose: () => void;
  // Called with `addedToPantry` indicating whether a lot was created.
  onDone: (args: { addedToPantry: boolean }) => void;
}

export function CheckoffModal({
  open,
  item,
  suggestions,
  onClose,
  onDone,
}: Props) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Form state.
  const [productId, setProductId] = useState<string | null>(null);
  const [barcode, setBarcode] = useState("");
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("");
  const [location, setLocation] = useState("");
  const [bestBefore, setBestBefore] = useState("");
  const [notes, setNotes] = useState("");
  const [substituteHint, setSubstituteHint] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset form whenever the modal opens with a new item.
  useEffect(() => {
    if (!open || !item) return;
    setErr(null);
    setSubstituteHint(null);
    setScannerOpen(false);
    setProductId(item.product_id);
    setBarcode(item.product?.barcode ?? "");
    setName(item.name);
    setBrand("");
    setCategory(item.effective_category ?? "");
    setImageUrl(item.product?.image_url ?? "");
    setQuantity(String(item.quantity ?? 1));
    setUnit(item.unit ?? "");
    setLocation("");
    setBestBefore("");
    setNotes("");
  }, [open, item]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  async function handleScan(scannedBarcode: string) {
    setScannerOpen(false);
    setBarcode(scannedBarcode);
    setErr(null);
    try {
      // Check the household catalog first.
      const known = await api.getProductByBarcode(scannedBarcode);
      if (known.product) {
        setProductId(known.product.id);
        setName(known.product.name);
        setBrand(known.product.brand ?? "");
        setCategory(known.product.category ?? category);
        setImageUrl(known.product.image_url ?? "");
        setUnit(known.product.default_unit ?? unit);
        // Pre-fill location from the substitute's most recent lot, unless
        // the user has already typed something into the field.
        if (!location && known.last_location) setLocation(known.last_location);
        setSubstituteHint(
          `Substitute: ${known.product.name} (already in your pantry catalog).`
        );
        return;
      }
      // Fall back to OpenFoodFacts.
      const lookup = await api.lookupBarcode(scannedBarcode);
      if (lookup.found) {
        setProductId(null); // new product — will be created on save
        setName(lookup.name ?? name);
        setBrand(lookup.brand ?? "");
        setCategory(lookup.category ?? category);
        setImageUrl(lookup.image_url ?? "");
        setUnit(lookup.default_unit ?? unit);
        setSubstituteHint(`Substitute: ${lookup.name ?? scannedBarcode} (from OpenFoodFacts).`);
      } else {
        setProductId(null);
        setSubstituteHint(
          "Substitute barcode saved, but not found in any catalog. Type the product name."
        );
      }
    } catch {
      setSubstituteHint("Lookup failed. Type the product name manually.");
    }
  }

  async function onSaveAndAdd() {
    if (!item) return;
    if (!name.trim()) {
      setErr("Please enter a product name.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // 1. Add a new inventory lot. The Worker creates the product on the fly
      //    if there's a barcode that doesn't yet exist.
      await api.addInventory({
        product_id: productId ?? undefined,
        barcode: barcode.trim() || undefined,
        name: name.trim(),
        brand: brand.trim() || undefined,
        category: category.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        quantity: parseFloat(quantity) || 1,
        unit: unit.trim() || undefined,
        best_before: bestBefore || null,
        location: location.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      // 2. Mark the grocery item as bought.
      await api.updateGroceryItem(item.id, { completed: true });
      onDone({ addedToPantry: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onJustMarkBought() {
    if (!item) return;
    setBusy(true);
    setErr(null);
    try {
      await api.updateGroceryItem(item.id, { completed: true });
      onDone({ addedToPantry: false });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!open || !item) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkoff-title"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white shadow-xl dark:bg-stone-900 sm:rounded-2xl"
      >
        <div className="border-b border-stone-200 p-4 dark:border-stone-800">
          <h2 id="checkoff-title" className="text-base font-semibold">
            Mark as bought
          </h2>
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Adjust the details before adding to your pantry.
          </p>
        </div>

        <div className="space-y-3 p-4">
          <button
            type="button"
            onClick={() => setScannerOpen(true)}
            className="btn-secondary w-full"
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 7V5a2 2 0 0 1 2-2h2M21 7V5a2 2 0 0 0-2-2h-2M3 17v2a2 2 0 0 0 2 2h2M21 17v2a2 2 0 0 1-2 2h-2M7 8v8M11 8v8M15 8v8M19 8v8" />
            </svg>
            Different product? Scan barcode
          </button>

          {substituteHint && (
            <p className="rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-900 dark:bg-brand-900/30 dark:text-brand-200">
              {substituteHint}
            </p>
          )}

          <div>
            <label className="label" htmlFor="co-name">Product name</label>
            <input
              id="co-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="label" htmlFor="co-category">Category</label>
            <input
              id="co-category"
              className="input"
              list="checkoff-category-suggestions"
              placeholder="Snacks, Dairy, Produce…"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
            <datalist id="checkoff-category-suggestions">
              {suggestions.categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="co-qty">Quantity</label>
              <input
                id="co-qty"
                type="number"
                min="0"
                step="any"
                className="input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="co-unit">Unit</label>
              <input
                id="co-unit"
                className="input"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="label" htmlFor="co-bb">Best before</label>
            <div className="flex items-center gap-2">
              <input
                id="co-bb"
                type="date"
                className="input max-w-[14rem]"
                value={bestBefore}
                onChange={(e) => setBestBefore(e.target.value)}
              />
              {bestBefore && (
                <button
                  type="button"
                  onClick={() => setBestBefore("")}
                  className="rounded-md px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
                  aria-label="Clear expiry date"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="co-loc">Location</label>
            <input
              id="co-loc"
              className="input"
              list="checkoff-location-suggestions"
              placeholder="Pantry, Fridge, Freezer…"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
            <datalist id="checkoff-location-suggestions">
              {suggestions.locations.map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="label" htmlFor="co-notes">Notes</label>
            <textarea
              id="co-notes"
              className="input min-h-[50px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
        </div>

        <div className="sticky bottom-0 space-y-2 border-t border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              onClick={onSaveAndAdd}
              disabled={busy}
            >
              {busy ? "Saving…" : "Add to pantry"}
            </button>
          </div>
          <button
            type="button"
            className="block w-full text-center text-xs text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
            onClick={onJustMarkBought}
            disabled={busy}
          >
            Just mark as bought (don't add to pantry)
          </button>
        </div>
      </div>

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={handleScan}
      />
    </div>
  );
}
